import React, { useState } from 'react';
import { useSearchBarContext } from '../../Context/SearchBarContext';
import {
  ESearchBarMode,
  SEARCH_LIMIT,
  SearchResult,
  getEffectiveSearchFilters,
  toSearchDetailDTO,
} from '../../Context/types';
import NodeComponent from 'components/customComponents/TreeControl/NodeComponent/NodeComponent';
import { getNodeLink } from 'components/customComponents/TreeControl/NodesGroupView/NodesGroupView';
import { getTreeNodeImageProps, openNode } from 'utils/Global';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import EViewType from 'enums/EViewType';
import SearchService from 'business/services/SearchService';
import { TreeNodeDTO } from 'business/models/Models';
import { ChevronRightIcon } from '@fmlx/icons';

const searchService = new SearchService();

const toTreeNode = (result: SearchResult) =>
  ({
    id: result.nodeId,
    name: result.name,
    type: result.type,
    datePrepared: result.datePrepared,
    extra: {
      id: result.entityId ?? result.nodeId,
      status: result.status,
      state: result.state,
      owner: result.owner,
    },
    allowEdits: false,
  } as TreeNodeDTO);

interface SearchResultsProps {
  onSearch: () => void;
}

export const SearchResults = ({ onSearch }: SearchResultsProps) => {
  const {
    results,
    filters,
    recordRecentSearch: recordRecentSearchInContext,
    setIsPanelOpen,
  } = useSearchBarContext();
  const [openedWindows, setOpenedWindows] = useState([]);
  const [showResultsHovered, setShowResultsHovered] = useState<boolean>(false);

  const recordRecentSearch = () => {
    // This panel only renders for Basic mode's live search results.
    const searchDetail = toSearchDetailDTO(
      getEffectiveSearchFilters(filters, ESearchBarMode.Basic),
    );
    recordRecentSearchInContext(searchDetail);
    searchService.RecordRecentSearch(searchDetail).catch(() => {});
  };

  const handleOpenResult = (result: SearchResult) => {
    const node = toTreeNode(result);
    const link = getNodeLink(node as any);
    if (!link) {
      return;
    }

    const openId =
      result.type === ETreeNodeTypes.Protein || result.type === ETreeNodeTypes.Seed
        ? result.nodeId
        : result.entityId ?? result.nodeId;
    openNode(link, link, openId, openedWindows, setOpenedWindows);
    recordRecentSearch();
    setIsPanelOpen(false);
  };

  const handleShowAllResults = () => {
    onSearch();
    setIsPanelOpen(false);
  };

  const visibleResults = results.slice(0, SEARCH_LIMIT);

  return (
    <div id="search-results-wrapper">
      <div id="search-results-list" className="flex flex-col px-2 pb-2">
        {visibleResults.map((result) => (
          <div
            key={`${result.type}-${result.nodeId}`}
            id={`search-results-item-${result.type}-${result.nodeId}`}
            className="rounded-sm hover:bg-[#f5f5f5]"
          >
            <NodeComponent
              viewType={EViewType.ListView}
              name={result.name}
              imageProps={getTreeNodeImageProps(toTreeNode(result))}
              selectedNodes={[]}
              node={toTreeNode(result)}
              selectedExplorerNode={null}
              onClick={() => handleOpenResult(result)}
            />
          </div>
        ))}
      </div>
      {results.length > SEARCH_LIMIT && (
        <div id="search-results-show-all-wrapper" className="flex justify-end">
          <div
            id="search-results-show-all-button"
            className="flex flex-row items-center text-[#F7A246] hover:text-[#CE873A]"
            onClick={(e) => {
              e.stopPropagation();
              handleShowAllResults();
            }}
            onMouseEnter={() => setShowResultsHovered(true)}
            onMouseLeave={() => setShowResultsHovered(false)}
          >
            <div className="font-[Roboto]">Show All Results</div>
            <ChevronRightIcon customColor={showResultsHovered ? '#CE873A' : '#F7A246'} />
          </div>
        </div>
      )}
    </div>
  );
};
